<template>
  <div class="login-container">
    <div class="login-box">
      <div class="login-title">
        <img src="../../assets/logo_l.png" alt="Logo" class="login-logo" />
      </div>
      <el-form
        ref="loginFormRef"
        :model="loginForm"
        :rules="loginRules"
        label-position="left"
        label-width="80px"
        class="login-form"
      >
        <!-- 登录类型切换 -->
        <!-- <el-radio-group v-model="isAdminLogin" class="login-type-switch">
          <el-radio-button label="true">管理员</el-radio-button>
          <el-radio-button label="false">员工</el-radio-button>
        </el-radio-group> -->

        <!-- 用户名/邮箱/手机号 -->
        <el-form-item label="用户名" prop="username">
          <el-input
            v-model="loginForm.username"
            placeholder="请输入用户名、邮箱或手机号"
            prefix-icon="User"
            clearable
            autocomplete="off"
            size="large"
          />
        </el-form-item>

        <!-- 密码 -->
        <el-form-item label="密码" prop="password">
          <el-input
            v-model="loginForm.password"
            type="password"
            placeholder="请输入密码"
            prefix-icon="Lock"
            show-password
            clearable
            size="large"
          />
        </el-form-item>

        <!-- 验证码 -->
        <el-form-item label="验证码" prop="captcha">
          <el-row :gutter="12">
            <el-col :span="14">
              <el-input
                v-model="loginForm.captcha"
                placeholder="请输入验证码"
                prefix-icon="Picture"
                clearable
                autocomplete="off"
                size="large"
              />
            </el-col>
            <el-col :span="10">
              <div class="captcha-image-container">
                <img
                  :src="captchaUrl"
                  alt="验证码"
                  class="captcha-image"
                  @click="refreshCaptcha"
                  :style="{ cursor: 'pointer' }"
                />
                <el-link type="primary" :underline="false" class="refresh-captcha" @click="refreshCaptcha" icon="Refresh"></el-link>
              </div>
            </el-col>
          </el-row>
        </el-form-item>

        <!-- 忘记密码 -->
        <el-form-item>
          <div class="login-actions">
            <el-link type="primary" :underline="false" class="forgot-password" @click="handleForgotPassword">
              忘记密码？
            </el-link>
          </div>
        </el-form-item>

        <!-- 登录按钮 -->
        <el-form-item>
          <el-button
            type="primary"
            :loading="loading"
            @click="handleLogin"
            class="login-button"
            round
            size="large"
          >
            {{ loading ? '登录中...' : '登录' }}
          </el-button>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
import { ref, reactive } from 'vue';
import { ElMessage } from 'element-plus';
import { useAuthStore } from '../../stores/auth';

export default {
  name: 'Login',
  setup() {
    const authStore = useAuthStore();
    const loginFormRef = ref(null);
    const loading = ref(false);

    // 登录表单数据
    const loginForm = reactive({
        username: '',
        password: '',
        captcha: '',
        captcha_key: '',
    });

    // 验证码URL
    const captchaUrl = ref('');

    // 生成唯一验证码key
    const generateCaptchaKey = () => {
        // 使用时间戳+随机数生成唯一key
        return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    };

    // 刷新验证码
    const refreshCaptcha = async () => {
        try {
            // 生成新的验证码key
            loginForm.captcha_key = generateCaptchaKey();
            // 动态导入axios
            const request = require('../../api/axios').default;
            // 通过API获取验证码
            const response = await request({
                url: `/manage/captcha`,
                method: 'get',
                params: {
                    key: loginForm.captcha_key,
                    time: Date.now()
                }
            });
            // 将base64格式的验证码设置为图片的src
            captchaUrl.value = response.data.captcha;
        } catch (error) {
            console.error('获取验证码失败:', error);
            // 失败时使用默认的验证码URL
            captchaUrl.value = `/manage/captcha?time=${Date.now()}&key=${loginForm.captcha_key}`;
        }
    };

    // 登录类型：true-管理员，false-员工
    const isAdminLogin = ref(true);

    // 登录表单规则
    const loginRules = {
        username: [
            { required: true, message: '请输入用户名、邮箱或手机号', trigger: 'blur' },
        ],
        password: [
            { required: true, message: '请输入密码', trigger: 'blur' },
            { min: 6, message: '密码长度不能少于6位', trigger: 'blur' },
        ],
        captcha: [
            { required: true, message: '请输入验证码', trigger: 'blur' },
            { min: 4, max: 4, message: '验证码长度为4位', trigger: 'blur' },
        ],
        captcha_key: [
            { required: true, message: '验证码key缺失', trigger: 'blur' },
        ],
    };

    // 登录处理
    const handleLogin = async () => {
      // 表单验证
      let valid = false;
      try {
        valid = await loginFormRef.value.validate();
      } catch (validationError) {
        // 表单验证失败，直接返回，ElForm 会自动显示验证错误
        return;
      }
      
      if (valid) {
        loading.value = true;
        try {
          // 调用登录方法
          await authStore.login(
            {
              username: loginForm.username,
              password: loginForm.password,
              captcha: loginForm.captcha,
              captcha_key: loginForm.captcha_key,
            },
            isAdminLogin.value ? 'admin' : 'employee'
          );

          ElMessage.success('登录成功');
          // 跳转到首页
          window.location.href = '/';
        } catch (error) {
          ElMessage.error('登录失败：' + (error.response?.data?.message || error.message || '未知错误'));
          // 登录失败时刷新验证码
          refreshCaptcha();
        } finally {
          loading.value = false;
        }
      }
    };

    // 忘记密码处理
    const handleForgotPassword = () => {
      ElMessage.info('请联系管理员重置密码');
    };

    // 组件挂载时初始化验证码
    refreshCaptcha();

    return {
      loginFormRef,
      loginForm,
      loginRules,
      isAdminLogin,
      loading,
      handleLogin,
      captchaUrl,
      refreshCaptcha,
      handleForgotPassword,
    };
  },
};
</script>

<style scoped>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-image: url('../../assets/login-background.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  padding: 20px;
  position: relative;
  width: 100%;
  /* 添加半透明黑色遮罩，确保文字清晰可见 */
  background-blend-mode: overlay;
  background-color: rgba(0, 0, 0, 0.2);
}

.login-box {
  width: 100%;
  max-width: 450px;
  background: rgba(255, 255, 255, 0.1); /* 增加透明度，更贴近背景图 */
  backdrop-filter: blur(10px); /* 添加毛玻璃效果 */
  border-radius: 20px; /* 稍大圆角，更现代化 */
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15); /* 更柔和的阴影 */
  padding: 40px; /* 增加内边距，更宽松的布局 */
  border: 1px solid rgba(255, 255, 255, 0.2); /* 半透明白色边框 */
  transition: all 0.3s ease;
  position: absolute;
  left: 75%; /* 从左侧60%位置开始，实现偏右效果 */
  top: 45%; /* 从顶部50%位置开始，实现居中效果 */
  transform: translate(-50%, -50%); /* 居中调整 */
}

.login-box:hover {
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.2); /* 更强的阴影效果 */
  background: rgba(255, 255, 255, 0.5); /* 悬停时仍保持一定透明度 */
  transform: translate(-50%, -50%) scale(1.02); /* 轻微放大效果 */
}

.login-title {
  text-align: center;
  margin-bottom: 30px;
}

.login-logo {
  width: 150px;
  height: auto;
  margin-bottom: 15px;
  display: block;
  margin-left: auto;
  margin-right: auto;
}

.login-title h2 {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.login-type-switch {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
  width: 100%;
}

.login-form {
  margin-top: 20px;
}

.login-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 10px;
}

.forgot-password {
  font-size: 14px;
}

.login-button {
  width: 100%;
  height: 45px;
  font-size: 16px;
  font-weight: 500;
}

/* 验证码样式 */
.captcha-image-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.captcha-image {
  width: 100%;
  height: 50px;
  border-radius: 4px;
  object-fit: cover;
  cursor: pointer;
  margin-right: 10px;
}

.refresh-captcha {
  font-size: 20px;
  cursor: pointer;
  margin-left: 5px;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
